<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Add Work Experience"
    ok-text="Save"
    @ok="handleStoreWorkExperience"
    @cancel="closeModal"
  >
    <a-form layout="horizontal" data-vv-scope="add-work-experience">
      <a-form v-if="selectableRoles.length > 0" data-vv-scope="tab-skills" layout="horizontal">
        <a-form-item :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }" label="Roles">
          <a-select
            v-model="newWorkExperience.role_ids"
            mode="multiple"
            show-search
            placeholder="Select roles"
            option-filter-prop="children"
          >
            <a-select-option v-for="r in selectableRoles" :key="r.id" :value="r.id">
              {{ r.title }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="
          $validator.errors.first('add-work-experience.company_name') ? 'error' : ''
        "
        :help="$validator.errors.first('add-work-experience.company_name')"
        label="Company"
      >
        <a-input
          v-model="newWorkExperience.company_name"
          v-validate="'required|max:255'"
          data-vv-name="company_name"
          data-vv-as="company name"
          placeholder="Name of the company"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('add-work-experience.title') ? 'error' : ''"
        :help="$validator.errors.first('add-work-experience.title')"
        label="Title"
      >
        <a-input
          v-model="newWorkExperience.title"
          v-validate="'required|max:255'"
          placeholder="Title of the job"
          data-vv-name="title"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('add-work-experience.description') ? 'error' : ''"
        :help="$validator.errors.first('add-work-experience.description')"
        label="Description"
      >
        <a-textarea
          v-model="newWorkExperience.description"
          v-validate="'required'"
          placeholder="Description of the job"
          data-vv-name="description"
        />
        <a
          v-if="!disableDescriptionBot"
          class="d-flex mb-3"
          :href="getDescriptionBotLink()"
          target="_blank"
          >Get help writing a description</a
        >
      </a-form-item>
      <a-form data-vv-scope="tab-skills" layout="horizontal">
        <a-form-item :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }" label="Skills">
          <a-select
            v-model="newWorkExperience.skill_ids"
            mode="multiple"
            show-search
            placeholder="Select skills"
            option-filter-prop="children"
          >
            <a-select-option
              v-for="s in skills"
              :key="s.id"
              :class="{ 'already-in': !!s.skill_id }"
            >
              {{ s.title }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('add-work-experience.start_date') ? 'error' : ''"
        :help="$validator.errors.first('add-work-experience.start_date')"
        label="Start date"
      >
        <a-date-picker
          v-validate="'required'"
          :value="parseDate(newWorkExperience.start_date)"
          :disabled-date="disableStartDate"
          data-vv-name="start_date"
          data-vv-as="start date"
          class="w-100"
          @change="(d) => onDateChange(d, 'start_date')"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('add-work-experience.end_date') ? 'error' : ''"
        :help="$validator.errors.first('add-work-experience.end_date')"
        label="End date"
      >
        <a-date-picker
          :value="parseDate(newWorkExperience.end_date)"
          :disabled-date="disableEndDate"
          class="w-100"
          @change="(d) => onDateChange(d, 'end_date')"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('add-work-experience.url_link') ? 'error' : ''"
        :help="$validator.errors.first('add-work-experience.url_link')"
        label="URL link"
      >
        <a-input
          v-model="newWorkExperience.url_link"
          v-validate="'max:255'"
          placeholder="URL link"
          data-vv-name="url_link"
          data-vv-as="link"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance, watch, computed } from "vue";
import { WorkExperienceInterface } from "../types";
import { SkillInterface } from "@/modules/skills/types";
import moment from "@/date";
import useFilters from "@/useFilters";
import { ProfileInterface } from "@/modules/authentication/types";
import { RoleSmallInterface } from "@/modules/roles/types";
const disableDescriptionBot = import.meta.env.VITE_DISABLE_DESCRIPTION_BOT;

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  profile: { type: Object as () => ProfileInterface, default: undefined },
  skills: { type: Array as () => Array<SkillInterface>, default: () => [] },
  roles: { type: Array as () => Array<RoleSmallInterface>, default: () => [] },
});

// Emits
const emits = defineEmits(["update-work-experience", "close"]);

// Filters
const { parseDate } = useFilters();

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;

// Data Properties
const newWorkExperience = ref<WorkExperienceInterface>({
  company_name: "",
  title: "",
  start_date: "",
  end_date: "",
  user: props.profile,
  description: undefined,
  skill_ids: undefined,
  url_link: undefined,
  role_ids: [],
});

const selectableRoles = computed(() => {
  return (props.roles as Array<any>).filter((role) => {
    // Prefer the actual attached list; counters can be stale when soft-deletes are involved.
    if (Array.isArray(role.work_experiences)) {
      return role.work_experiences.length === 0;
    }

    return true;
  });
});

const getDateIfValid = (dateValue: string | null | undefined): string | null => {
  const today = moment().endOf("day");
  if (!dateValue) {
    return null;
  }

  const parsedDate = moment(dateValue);
  return parsedDate.isAfter(today) ? null : dateValue;
};

const getEarliestStartDate = (selectedRoles: Array<any>): string | null => {
  const startDates = selectedRoles
    .map((role) => getDateIfValid(role.start_date))
    .filter((dateValue): dateValue is string => !!dateValue);

  if (!startDates.length) {
    return null;
  }

  return startDates.reduce((earliest, current) =>
    moment(current).isBefore(moment(earliest)) ? current : earliest
  );
};

const getLatestEndDate = (selectedRoles: Array<any>): string | null => {
  const endDates = selectedRoles
    .map((role) => role.end_date)
    .filter((dateValue): dateValue is string => !!dateValue);

  if (!endDates.length) {
    return null;
  }

  const latestEndDate = endDates.reduce((latest, current) =>
    moment(current).isAfter(moment(latest)) ? current : latest
  );

  return moment(latestEndDate).isAfter(moment().endOf("day")) ? null : latestEndDate;
};

const prefillFromRoles = (roleIds: Array<number>): void => {
  const selectedRoles = roleIds
    .map((roleId) => props.roles.find((role) => role.id === roleId) as any)
    .filter((role) => !!role);

  if (!selectedRoles.length) {
    return;
  }

  const firstRoleWithValue = (getValue: (role: any) => any): any => {
    for (const role of selectedRoles) {
      const value = getValue(role);
      if (value === null || value === undefined) {
        continue;
      }
      if (typeof value === "string" && value.trim() === "") {
        continue;
      }
      if (Array.isArray(value) && value.length === 0) {
        continue;
      }

      return value;
    }

    return null;
  };

  const companyName = firstRoleWithValue(
    (role) => role.project?.client?.name || role.project?.name || null
  );
  const title = firstRoleWithValue((role) => role.title || null);
  const description = firstRoleWithValue((role) => role.description || null);
  const startDate = getEarliestStartDate(selectedRoles);
  const endDate = getLatestEndDate(selectedRoles);
  const skillIds = Array.from(
    new Set(
      selectedRoles.flatMap((role) =>
        Array.isArray(role.skills)
          ? role.skills
              .map((skill: any) => skill?.id)
              .filter((skillId: number | undefined) => !!skillId)
          : []
      )
    )
  );
  const existingSkillIds = Array.isArray(newWorkExperience.value.skill_ids)
    ? newWorkExperience.value.skill_ids
    : [];

  newWorkExperience.value.company_name = companyName || "";
  newWorkExperience.value.title = title || "";
  newWorkExperience.value.description = description || "";
  newWorkExperience.value.start_date = startDate;
  newWorkExperience.value.end_date = endDate;
  newWorkExperience.value.skill_ids = Array.from(new Set([...existingSkillIds, ...skillIds]));
};

watch(
  () => newWorkExperience.value.role_ids,
  (roleIds) => {
    if (!Array.isArray(roleIds) || roleIds.length === 0) {
      return;
    }

    prefillFromRoles(roleIds as Array<number>);
  }
);

const onDateChange = (value: any, attribute: string): void => {
  (newWorkExperience.value as any)[attribute] = value ? value.format("YYYY-MM-DD") : null;
};

// Cannot have start_date in future or after end_date.
const disableStartDate = (current: any): any => {
  const today = moment().endOf("day");
  const isAfterEndDate =
    current &&
    newWorkExperience.value.end_date &&
    current > moment(newWorkExperience.value.end_date).endOf("day");
  const isInFuture = current && moment(current).isAfter(today);
  return isAfterEndDate || isInFuture;
};

// Cannot have end_date in future or before start_date.
const disableEndDate = (current: any): any => {
  const today = moment().endOf("day");
  const isBeforeStartDate =
    current &&
    newWorkExperience.value.start_date &&
    current < moment(newWorkExperience.value.start_date).startOf("day");
  const isInFuture = current && moment(current).isAfter(today);
  return isBeforeStartDate || isInFuture;
};

const handleStoreWorkExperience = (): void => {
  $validator?.validateAll("add-work-experience").then((result) => {
    if (result) {
      emits("store-work-experience", newWorkExperience.value);
    }
  });
};

const closeModal = (): void => {
  emits("close");
};

const getDescriptionBotLink = (): string => {
  return `https://role-describer.ai.skry.se/?user_id=${props.profile?.id}&work_experience_id=NULL&embed=true`;
};
</script>
